import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api/http-client';

import type {
    SavePracticeLocationInput,
    UpdatePracticeInput,
    UpdatePracticeLocationInput,
} from '../model/practice.types';
import { practiceApi } from './practice.api';
import { queryKeys } from './practice.queries';

// A location command can fail because the target is already gone (404) or it lost a race on the
// one-default index (409) — either way the cached list is stale, so refetch it.
function invalidateLocationsOnStale(queryClient: ReturnType<typeof useQueryClient>) {
    return (error: unknown) => {
        if (error instanceof ApiError && (error.status === 404 || error.status === 409)) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.locations() });
        }
    };
}

export function useUpdatePracticeOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (input: UpdatePracticeInput) => practiceApi.update(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.details() });
        },
    });
}

export function useAddPracticeLocationOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (input: SavePracticeLocationInput) => practiceApi.addLocation(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.locations() }),
        onError: invalidateLocationsOnStale(queryClient),
    });
}

export function useUpdatePracticeLocationOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: ({ locationId, ...data }: UpdatePracticeLocationInput) =>
            practiceApi.updateLocation(locationId, data),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.locations() });
        },
        onError: invalidateLocationsOnStale(queryClient),
    });
}

export function useRemovePracticeLocationOptions() {
    const queryClient = useQueryClient();
    const invalidateOnStale = invalidateLocationsOnStale(queryClient);
    return mutationOptions({
        mutationFn: (locationId: string) => practiceApi.removeLocation(locationId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.locations() });
        },
        onError: (error) => {
            invalidateOnStale(error);
            console.error('Failed to delete location:', error);
            toast.error('Failed to delete location');
        },
    });
}

export function useSetDefaultPracticeLocationOptions() {
    const queryClient = useQueryClient();
    const invalidateOnStale = invalidateLocationsOnStale(queryClient);
    return mutationOptions({
        mutationFn: (locationId: string) => practiceApi.setDefaultLocation(locationId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.locations() });
        },
        onError: (error) => {
            invalidateOnStale(error);
            console.error('Failed to set default location:', error);
            toast.error('Failed to set default location');
        },
    });
}
